import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const EnhancedApplicationFormsHub = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const formCategories = [
    { id: 'all', name: 'All Forms', icon: 'FileText', count: 24 },
    { id: 'onboarding', name: 'Employee Onboarding', icon: 'UserPlus', count: 5 },
    { id: 'leave', name: 'Leave Applications', icon: 'Clock', count: 4 },
    { id: 'compliance', name: 'Compliance Forms', icon: 'Shield', count: 6 },
    { id: 'benefits', name: 'Benefits Enrollment', icon: 'Heart', count: 4 },
    { id: 'performance', name: 'Performance Management', icon: 'TrendingUp', count: 5 }
  ];

  const formLibrary = [
    {
      id: 'nasfund_registration',
      name: 'NASFUND Member Registration',
      category: 'onboarding',
      description: 'Complete employee enrollment for PNG superannuation compliance',
      status: 'available',
      completionRate: 0,
      estimatedTime: '15-20 min',
      icon: 'FileText',
      route: '/nasfund-member-registration-forms',
      features: ['Auto-save', 'Multi-step', 'Document upload', 'Validation']
    },
    {
      id: 'employee_onboarding',
      name: 'New Employee Onboarding',
      category: 'onboarding',
      description: 'Comprehensive onboarding form with personal details and employment information',
      status: 'available',
      completionRate: 0,
      estimatedTime: '10-15 min',
      icon: 'UserPlus',
      route: '/workforce-management-dashboard',
      features: ['Auto-save', 'Pre-population', 'Validation']
    },
    {
      id: 'annual_leave',
      name: 'Annual Leave Application',
      category: 'leave',
      description: 'Request annual leave with automatic balance checking and manager approval',
      status: 'available',
      completionRate: 0,
      estimatedTime: '5 min',
      icon: 'Calendar',
      route: '/employee-self-service-portal',
      features: ['Balance check', 'Auto-routing', 'Email notifications']
    },
    {
      id: 'sick_leave',
      name: 'Sick Leave Application',
      category: 'leave',
      description: 'Submit sick leave request with medical certificate upload',
      status: 'available',
      completionRate: 0,
      estimatedTime: '5 min',
      icon: 'Heart',
      route: '/employee-self-service-portal',
      features: ['Document upload', 'Compliance checking', 'Auto-routing']
    },
    {
      id: 'contract_review',
      name: 'Employment Contract Review',
      category: 'compliance',
      description: 'Review and sign employment contracts with compliance scanning',
      status: 'available',
      completionRate: 0,
      estimatedTime: '10 min',
      icon: 'FileCheck',
      route: '/contract-management',
      features: ['AI scanning', 'Digital signature', 'Audit trail']
    },
    {
      id: 'performance_review',
      name: 'Performance Review Form',
      category: 'performance',
      description: '360-degree feedback with goal-setting and competency assessment',
      status: 'available',
      completionRate: 0,
      estimatedTime: '20-30 min',
      icon: 'TrendingUp',
      route: '/employee-management',
      features: ['360-feedback', 'Goal tracking', 'Automated reminders']
    },
    {
      id: 'benefits_enrollment',
      name: 'Benefits Enrollment',
      category: 'benefits',
      description: 'Enroll in company benefits with plan comparisons and cost calculators',
      status: 'available',
      completionRate: 0,
      estimatedTime: '15 min',
      icon: 'Gift',
      route: '/company-settings',
      features: ['Plan comparison', 'Cost calculator', 'Eligibility check']
    },
    {
      id: 'payroll_update',
      name: 'Payroll Information Update',
      category: 'compliance',
      description: 'Update bank details, tax information, and salary allocation',
      status: 'available',
      completionRate: 0,
      estimatedTime: '5-10 min',
      icon: 'DollarSign',
      route: '/payroll-management',
      features: ['Secure', 'Validation', 'Audit trail']
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'available', label: 'Available' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  const filteredForms = formLibrary?.filter(form => {
    const matchesSearch = form?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         form?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || form?.category === selectedCategory;
    const matchesStatus = selectedStatus === 'all' || form?.status === selectedStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleFormClick = (form) => {
    navigate(form?.route);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-emerald-100 text-emerald-700', label: 'Available' },
      in_progress: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      completed: { color: 'bg-gray-100 text-gray-700', label: 'Completed' }
    };
    const config = statusConfig?.[status] || statusConfig?.available;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-3 rounded-xl shadow-lg mr-4">
              <Icon name="FileText" size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Enhanced Application Forms Hub
              </h1>
              <p className="text-gray-600 mt-1">
                Centralized form management with improved user experience and comprehensive capabilities
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Category Navigation - 3 cols */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Form Categories</h2>
              <div className="space-y-2">
                {formCategories?.map(category => (
                  <button
                    key={category?.id}
                    onClick={() => setSelectedCategory(category?.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      selectedCategory === category?.id
                        ? 'bg-purple-100 text-purple-900 font-medium' :'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon name={category?.icon} size={18} className="mr-3" />
                      <span className="text-sm">{category?.name}</span>
                    </div>
                    <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                      {category?.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Stats</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Forms</span>
                    <span className="font-semibold text-gray-900">24</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Completed</span>
                    <span className="font-semibold text-emerald-600">0</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">In Progress</span>
                    <span className="font-semibold text-blue-600">0</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Forms Area - 9 cols */}
          <div className="col-span-12 lg:col-span-9">
            {/* Search and Filters */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search forms by name or description..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={(value) => setSelectedStatus(value)}
                  placeholder="Filter by status"
                />
              </div>
            </div>

            {/* Featured Improvements Banner */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-lg p-6 mb-6 text-white">
              <div className="flex items-start">
                <Icon name="Sparkles" size={24} className="mr-4 mt-1" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Enhanced Form Features</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center">
                      <Icon name="Save" size={16} className="mr-2" />
                      <span>Auto-save functionality</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="Zap" size={16} className="mr-2" />
                      <span>Smart field pre-population</span>
                    </div>
                    <div className="flex items-center">
                      <Icon name="CheckCircle" size={16} className="mr-2" />
                      <span>Real-time validation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Forms Grid */}
            {filteredForms?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredForms?.map(form => (
                  <div
                    key={form?.id}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all cursor-pointer border-2 border-transparent hover:border-purple-200"
                    onClick={() => handleFormClick(form)}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <div className="bg-purple-100 p-3 rounded-lg mr-3">
                            <Icon name={form?.icon} size={24} className="text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{form?.name}</h3>
                            <p className="text-xs text-gray-500">{form?.estimatedTime}</p>
                          </div>
                        </div>
                        {getStatusBadge(form?.status)}
                      </div>

                      <p className="text-sm text-gray-600 mb-4">
                        {form?.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {form?.features?.slice(0, 3)?.map((feature, index) => (
                          <span
                            key={index}
                            className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Progress Bar (if in progress) */}
                      {form?.completionRate > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Progress</span>
                            <span>{form?.completionRate}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ width: `${form?.completionRate}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleFormClick(form)}
                      >
                        {form?.completionRate > 0 ? 'Continue Form' : 'Start Form'}
                        <Icon name="ArrowRight" size={16} className="ml-2" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Icon name="Search" size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No forms found</h3>
                <p className="text-gray-600">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            )}

            {/* Form Builder CTA */}
            <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="bg-amber-100 p-3 rounded-lg mr-4">
                    <Icon name="Wrench" size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Advanced Form Builder</h3>
                    <p className="text-sm text-gray-600">
                      Create custom forms with drag-and-drop interface and conditional logic
                    </p>
                  </div>
                </div>
                <Button variant="primary">
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Custom Form
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Information Banner */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start">
            <Icon name="Info" size={20} className="text-blue-600 mr-3 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-2">PNG Employment Act Compliance</p>
              <p className="text-sm text-blue-700">
                All forms include automatic PNG Employment Act compliance checking, real-time validation, 
                and intelligent field pre-population using existing employee data. Forms are automatically 
                saved as you progress, preventing data loss.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedApplicationFormsHub;